import styled from 'styled-components';
import DotLoader from '@svg/dot_loader';

import { MessageChat } from './MessageChat';
import { createRef, MutableRefObject, useEffect, useState } from 'react';
import { Alert, Badge, IconButton, InputBase, Popover, Stack } from '@mui/material';
import { ImageOutlined, Send } from '@mui/icons-material';
import { MediaUrl } from '@components/Flow/Nodes/types';
import { MessageMediaAttach } from './MessageMediaAttach';
import { AppDispatch, State, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from '@common/hooks/useTranslate';
import { useParams } from 'react-router-dom';
import { isNodeError, MessageParameters, runWorkflow } from '@store/async/run';
import { MessagingChatMessage } from './message.types';
import { v4 as uuidv4 } from 'uuid';
import { WorkflowExecutionType } from '@common/Node/NodeSidebar/NodeExecutionHeader';
import { useEntityExecution } from '@common/hooks/useEntityExecution';
import { useSnackbar } from 'notistack';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  // total - topnav - sidebar header - first tab row - second tab row;
  height: calc(100vh - 60px - 61px - 52px);
`;

const MessageContainer = styled.div`
  flex: 1;
  word-break: break-word;
  overflow-y: auto;
  padding: 0.5rem 1.5rem;
  margin-bottom: 105px;
`;

const MessageFooter = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  position: absolute;
  bottom: 0;
  height: 105px;
  border-top: 1px solid ${({ theme }) => theme.line.light};
  background-color: ${({ theme }) => theme.background.standard};
`;

const MessageChatInput = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 80%;
  min-width: 80%;
  background-color: ${({ theme }) => theme.background.standard};
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 5px;
  padding: 0.35rem 0.5rem;
  padding-right: 1rem;
  margin-bottom: 0rem;

  box-shadow: 0px 6px 15px -1px rgba(1, 1, 1, 0.2);

  svg {
    color: ${({ theme }) => theme.text.standard};
  }
`;

const MessageChatAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  width: 22px;
`;

interface Props {
  abortControllerRef: MutableRefObject<AbortController | null>;
  start: () => void;
  pause: () => void;
  reset: () => void;
}

export const MessageChats: React.FC<Props> = ({ abortControllerRef, start, pause, reset }) => {
  const dispatch = useDispatch<AppDispatch>();
  const bottomRef = createRef<HTMLDivElement>();

  const messages = useSelector((state: State) => state.tabs.active.run.messagingChat);
  const trace = useSelector((state: State) => state.tabs.active.run);

  const { translate } = useTranslate();
  const { service_id, workflow_id } = useParams();
  const { entity } = useEntityExecution(WorkflowExecutionType.InboundMessage);
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [text, setText] = useState<string>('');
  const [alt, setAlt] = useState(false);
  const [media, setMedia] = useState<MediaUrl[]>([]);

  const isNoResponse = messages.length === 1 && !trace?.running;
  const hasMedia = media.some((item) => item.value);
  const open = Boolean(anchorEl);
  const id = open ? 'media-popover' : undefined;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    reset();

    const workspace_id = service_id;
    const translation = translate(workflow_id || '');

    setText('');
    setMedia([]);
    start();

    abortControllerRef.current = new AbortController();

    const messageParameters: MessageParameters = {
      type: hasMedia ? 'MMS' : 'SMS',
      text,
      subject: hasMedia ? 'From Flow' : '',
      media_urls: media.map((item) => item.value),
      entity_id: entity?.webhook_id,
    };

    const parameters = { body: null, query: null, queryData: null, messageParameters };

    const message: MessagingChatMessage = {
      id: uuidv4(),
      from: '',
      media_urls: media.map((item) => item.value),
      sent_at: new Date().toISOString(),
      source: 'receiver',
      text,
    };

    dispatch(tabActions.messagingAddMessage(message));

    const response = await dispatch(
      runWorkflow({
        workspace_id,
        workflow_id,
        parameters,
        translation,
        abortController: abortControllerRef.current,
      })
    );

    pause();

    if (isNodeError(response.payload)) {
      enqueueSnackbar(response.payload.error.execution.reason, { variant: 'error', autoHideDuration: 8000 });
      enqueueSnackbar(`Workflow Execution Failed`, { variant: 'error', autoHideDuration: 3000 });
    }
  };

  return (
    <Container>
      <MessageContainer>
        {isNoResponse && (
          <Alert sx={{ marginTop: '1rem' }} severity='info'>
            If you respond to the incoming message with the Send Message node, you will see your chat history below.
          </Alert>
        )}
        {messages.map((message, index) => (
          <MessageChat key={index} message={message} />
        ))}
        <div ref={bottomRef} />
      </MessageContainer>
      {!isNoResponse && (
        <MessageFooter>
          <MessageChatInput>
            <InputBase
              maxRows={4}
              disabled={isNoResponse}
              multiline
              onKeyDown={(e) => {
                if (e.key === 'Control' || e.key === 'Shift') return setAlt(true);
                if (e.key === 'Enter' && alt) return;
                if (e.key !== 'Enter') return;
                if (text.trim() === '') return;
                if (isNoResponse) return;
                if (trace?.running) return;

                e.preventDefault();
                e.stopPropagation();

                sendMessage();
              }}
              onKeyUp={(e) => {
                if (e.key === 'Control' || e.key === 'Shift') return setAlt(false);
              }}
              sx={{ ml: 1, flex: 1 }}
              placeholder='Send a message...'
              value={text}
              onChange={(e) => setText(e?.target?.value)}
            />
            <Stack direction='row' alignItems='center' spacing={0.25}>
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} disabled={isNoResponse}>
                <Badge badgeContent={media.length} color='primary'>
                  <MessageChatAction>
                    <ImageOutlined sx={{ fontSize: '1.6ch' }} />
                  </MessageChatAction>
                </Badge>
              </IconButton>
              {trace?.running ? (
                <IconButton disabled>
                  <MessageChatAction>
                    <DotLoader height={6} />
                  </MessageChatAction>
                </IconButton>
              ) : (
                <IconButton onClick={sendMessage} disabled={isNoResponse || !text || trace?.running}>
                  <MessageChatAction>
                    <Send sx={{ fontSize: '1.4ch' }} />
                  </MessageChatAction>
                </IconButton>
              )}
            </Stack>
          </MessageChatInput>
        </MessageFooter>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: -20,
          horizontal: -120,
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <MessageMediaAttach media={media} setMedia={setMedia} />
      </Popover>
    </Container>
  );
};
